/**
 * Application configuration loader (frontend equivalent of backend "appsettings.json").
 *
 * GOAL
 * ----
 * - Provide a single place to read environment-specific settings (API base URL, feature flags, etc.)
 * - Allow changing these settings *after* the frontend build, just by editing `public/appsettings.json`.
 * - Avoid hardcoding URLs like "https://localhost:7249/api" inside the source code.
 *
 * HOW IT WORKS
 * ------------
 * 1. At application startup, you call `loadAppSettings()` once (typically in App.jsx inside useEffect).
 * 2. This function does a fetch on `/appsettings.json` (served from the /public folder).
 * 3. The JSON is stored in an in-memory variable (`appSettings`) so it can be accessed synchronously later.
 * 4. Anywhere in the code, you can then call:
 *      - getAppSettings()   -> returns the whole settings object.
 *      - getApiBaseUrl()    -> returns specifically the API base URL.
 *
 * IMPORTANT
 * ---------
 * - `loadAppSettings()` MUST be called before `getAppSettings()` / `getApiBaseUrl()`.
 *   Otherwise, `getAppSettings()` will throw an error to avoid silently using undefined config.
 * - The file `public/appsettings.json` must exist and contain at least:
 *
 *      {
 *        "API_BASE_URL": "https://localhost:7249/api"
 *      }
 *
 * - On each environment (Dev, QA, Staging, Prod), you can deploy the SAME build
 *   and only change `appsettings.json` to point to the correct backend.
 */

// Holds the loaded configuration in memory.
// We start as `null` and fill it once `loadAppSettings()` successfully runs.
let appSettings = null;

/**
 * Asynchronously loads the application settings from `/appsettings.json`.
 *
 * - This function should be called once at app startup.
 * - If called multiple times, it will simply return the already-cached object.
 * - If the fetch fails or the JSON is invalid, it throws an Error.
 *
 * @returns {Promise<object>} The loaded settings object
 */
export async function loadAppSettings() {
  // If we already loaded the settings once, just reuse them.
  if (appSettings) {
    return appSettings;
  }

  let response;

  try {
    // Fetch the JSON from the public root.
    // The cache: "no-store" flag tells the browser not to use a stale cached copy,
    // which is useful when you want to tweak appsettings.json without clearing cache.
    response = await fetch("/appsettings.json", {
      cache: "no-store",
      headers: {
        Accept: "application/json",
      },
    });
  } catch (error) {
    // Network-level error: DNS issue, server down, etc.
    console.error("Failed to fetch appsettings.json (network error):", error);
    throw new Error("Network error while loading appsettings.json");
  }

  if (!response.ok) {
    // HTTP-level error: 404, 500, etc.
    const message = `Failed to load appsettings.json (status ${response.status})`;
    console.error(message);
    throw new Error(message);
  }

  try {
    // Attempt to parse the JSON.
    appSettings = await response.json();
  } catch (error) {
    // JSON format error: invalid syntax or corrupt file.
    console.error("Invalid JSON in appsettings.json:", error);
    throw new Error("Invalid JSON in appsettings.json");
  }

  // Basic validation: ensure there is at least an API_BASE_URL string.
  // You can extend this with more fields as your config grows.
  if (
    !appSettings ||
    typeof appSettings.API_BASE_URL !== "string" ||
    appSettings.API_BASE_URL.trim() === ""
  ) {
    const message =
      'appsettings.json must contain a non-empty string property "API_BASE_URL".';
    console.error(message, appSettings);
    throw new Error(message);
  }

  return appSettings;
}

/**
 * Synchronously returns the already-loaded settings object.
 *
 * This function assumes that `loadAppSettings()` has been called BEFOREHAND.
 * If it hasn't, we throw an error to avoid continuing with an undefined configuration.
 *
 * @returns {object} The previously loaded settings object.
 */
export function getAppSettings() {
  if (!appSettings) {
    throw new Error(
      "App settings have not been loaded yet. " +
      "Call loadAppSettings() once at application startup " +
      "(for example, inside App.jsx useEffect) before using getAppSettings()."
    );
  }

  return appSettings;
}

/**
 * Convenience helper to directly obtain the API base URL.
 *
 * This is simply a wrapper around `getAppSettings().API_BASE_URL`, but it
 * centralizes the name of the property so that if you rename it in the future,
 * you only need to change it in one place.
 *
 * @returns {string} The API base URL defined in appsettings.json.
 */
export function getApiBaseUrl() {
  return getAppSettings().API_BASE_URL;
}

/**
 * ESRI / ArcGIS portal base URL.
 *
 * @returns {string}
 */
export function getEsriPortalUrl() {
  return getAppSettings().ESRI_PORTAL_URL;
}

/**
 * ESRI API key (may be empty).
 *
 * @returns {string}
 */
export function getEsriApiKey() {
  return getAppSettings().ESRI_API_KEY;
}

/**
 * ESRI Portal Web Map ID.
 *
 * @returns {string}
 */
export function getEsriPortalMapId() {
  return getAppSettings().ESRI_PORTAL_MAP_ID;
}

/**
 * ESRI Portal Web Scene ID.
 *
 * @returns {string}
 */
export function getEsriPortalSceneId() {
  return getAppSettings().ESRI_PORTAL_SCENE_ID;
}
